/**
 * Распознавание штрихкода (SPEC §5 N2, §8).
 *
 * Нативный `BarcodeDetector` есть в Chrome и Android WebView, но его нет в
 * WebKit — то есть в мини-аппе на iPhone. Поэтому вторым движком идёт ZXing
 * (WASM): он грузится динамически и только там, где нативного API нет, так что
 * основной бандл не тяжелеет.
 *
 * Сам .wasm лежит рядом со статикой приложения, а не на CDN: мини-апп должен
 * работать под строгим CSP и не ходить на сторонние домены.
 */

import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

/** Форматы продуктовых штрихкодов. QR и прочее намеренно не ищем. */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'itf'] as const;

/**
 * Кадр обрабатываем не целиком, а полосой вокруг рамки прицела: там и лежит
 * штрихкод. Пикселей меньше, а деталей на самом коде больше — это главное,
 * от чего зависит, распознается он с первого кадра или с десятого.
 */
const CROP = { x: 0.08, y: 0.18, width: 0.84, height: 0.40 };
/** Верхняя граница стороны обрабатываемого куска: дальше растёт только время. */
const MAX_SIDE = 1100;

export type BarcodeEngine = 'native' | 'wasm' | 'none';

interface DetectorLike {
  detect: (source: CanvasImageSource | ImageData) => Promise<{ rawValue: string }[]>;
}

export interface BarcodeReader {
  engine: BarcodeEngine;
  /** Возвращает найденный код из кадра видео или null. */
  read: (video: HTMLVideoElement) => Promise<string | null>;
}

declare global {
  interface Window {
    BarcodeDetector?: {
      new (options?: { formats?: readonly string[] }): DetectorLike;
      getSupportedFormats?: () => Promise<string[]>;
    };
  }
}

async function nativeDetector(): Promise<DetectorLike | null> {
  const Native = window.BarcodeDetector;
  if (!Native) return null;
  try {
    // Наличие класса ещё не значит поддержку нужных форматов: на части
    // Android-сборок список пустой или состоит из одного QR.
    const supported = (await Native.getSupportedFormats?.()) ?? [];
    if (supported.length > 0 && !supported.includes('ean_13')) return null;
    return new Native({ formats: FORMATS });
  } catch {
    return null;
  }
}

async function wasmDetector(): Promise<DetectorLike | null> {
  try {
    const { BarcodeDetector, setZXingModuleOverrides } = await import(
      'barcode-detector/ponyfill'
    );
    setZXingModuleOverrides({
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? wasmUrl : `${prefix}${path}`,
    });
    return new BarcodeDetector({ formats: [...FORMATS] }) as unknown as DetectorLike;
  } catch {
    return null;
  }
}

/** Штрихкод товара — 8–14 цифр; всё остальное игнорируем. */
export function isProductBarcode(value: string): boolean {
  return /^\d{8,14}$/.test(value);
}

export async function createBarcodeReader(): Promise<BarcodeReader> {
  const native = await nativeDetector();
  const detector = native ?? (await wasmDetector());
  const engine: BarcodeEngine = native ? 'native' : detector ? 'wasm' : 'none';

  if (!detector) {
    return { engine: 'none', read: async () => null };
  }

  let canvas: HTMLCanvasElement | null = null;
  let useCrop = true;

  /** Рисует на канве либо полосу прицела, либо весь кадр целиком. */
  function draw(video: HTMLVideoElement, cropped: boolean): HTMLCanvasElement | null {
    const sx = cropped ? Math.round(video.videoWidth * CROP.x) : 0;
    const sy = cropped ? Math.round(video.videoHeight * CROP.y) : 0;
    const sw = cropped ? Math.round(video.videoWidth * CROP.width) : video.videoWidth;
    const sh = cropped ? Math.round(video.videoHeight * CROP.height) : video.videoHeight;

    const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
    canvas ??= document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  const read = async (video: HTMLVideoElement): Promise<string | null> => {
    if (video.readyState < 2 || !video.videoWidth) return null;

    // Чередуем полосу прицела и полный кадр: первая быстрее и точнее, второй
    // спасает, когда код держат мимо рамки.
    const frame = draw(video, useCrop);
    useCrop = !useCrop;
    if (!frame) return null;

    try {
      const codes = await detector.detect(frame);
      const found = codes.find((code) => isProductBarcode(code.rawValue));
      return found?.rawValue ?? null;
    } catch {
      return null;
    }
  };

  return { engine, read };
}
